/** @jsxImportSource @emotion/react */
import React from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'
import { ProductMonthlyBucket } from '../orders/models/dashboard-stats.type'
import { CHART_COLORS, formatInt, formatMonthLabel } from './dashboard-helpers'
import { DashboardChartCard } from './dashboard-chart-card'

type Props = {
    data: ProductMonthlyBucket[]
}

type PivotRow = { label: string } & { [productName: string]: number | string }

export const ProductMonthComparisonChart: React.FC<Props> = ({ data }) => {
    const { t } = useTranslation()

    const monthKeys = Array.from(new Set(data.map((d) => d.label))).sort()
    const productNames = Array.from(new Set(data.map((d) => d.productName)))

    const rows: PivotRow[] = monthKeys.map((monthKey) => {
        const row: PivotRow = { label: formatMonthLabel(monthKey) }
        productNames.forEach((name) => {
            row[name] = 0
        })
        data
            .filter((d) => d.label === monthKey)
            .forEach((d) => {
                row[d.productName] = (row[d.productName] as number) + d.count
            })
        return row
    })

    return (
        <DashboardChartCard
            title={t('TEXT_PRODUCT_MONTH_COMPARISON', 'Produkter måned for måned')}
            isEmpty={rows.length === 0}
            emptyText={t('TEXT_NO_DATA', 'Ingen data for valgt periode')}
        >
            <ResponsiveContainer width="100%" height={320}>
                <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip formatter={(value: number) => formatInt(value)} />
                    <Legend />
                    {productNames.map((name, index) => (
                        <Bar key={name} dataKey={name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </DashboardChartCard>
    )
}
